import { keyBy, omit } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { HubspotResourceMapping, HubspotSyncTypes } from 'api/integrations';
import { Option, ISingleValue as SingleValue } from 'components/atoms/select';
import { toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';
import {
  useHubspotDeleteMappingMutation,
  useHubspotDeleteResourceMutation,
  useHubspotExternalResourceTypes,
  useHubspotMappingCreateMutation,
  useHubspotMappingUpdateMutation,
  useHubspotResourceMappings,
} from 'queries/hubspot-integration';
import { numberSorter } from 'utils/sorters';
import {
  ByHubspotResourceMap,
  HubspotFieldMappingLocalData,
  HubspotResourceMappingLocalData,
  PartialHubspotFieldMappingLocalData,
} from '../types';
import { getNewMap, serialiseMappingData } from './utils';

export const syncOptions = [
  {
    label: HubspotSyncTypes.SyncExternalToNative,
    value: HubspotSyncTypes.SyncExternalToNative,
  },
  {
    label: HubspotSyncTypes.SyncTwoWay,
    value: HubspotSyncTypes.SyncTwoWay,
  },
  {
    label: HubspotSyncTypes.SyncNativeToExternal,
    value: HubspotSyncTypes.SyncNativeToExternal,
  },
];

export const useFieldMappingActions = (integrationId: number) => {
  const successTitle = useTranslation('success');
  const errorTitle = useTranslation('failure');
  const errorMessage = useTranslation('error');
  const resourceDeleteSuccessMessage = useTranslation(
    'settings.integrations.sf.resource_mappings.deleted.message',
  );
  const mappingDeleteSuccessMessage = useTranslation(
    'settings.integrations.sf.resource_mappings.field.toast.deleted.message',
  );
  const mappingCreateErrorMessage = useTranslation(
    'settings.integrations.sf.resource_mappings.mapping.create.error.message',
  );
  const mappingUpdateErrorMessage = useTranslation(
    'settings.integrations.sf.resource_mappings.mapping.update.error.message',
  );

  const [resources, setResources] = useState<ByHubspotResourceMap>({});
  const { data, status: resourceMappingsStatus } =
    useHubspotResourceMappings(integrationId);
  const { data: resourceTypeList, status: resourceTypeStatus } =
    useHubspotExternalResourceTypes(integrationId);
  const { mutate: deleteResourceMutation } = useHubspotDeleteResourceMutation(
    Number(integrationId),
  );
  const { mutate: deteleMappingMutation } =
    useHubspotDeleteMappingMutation(integrationId);

  const { mutate: createMappingMutation } =
    useHubspotMappingCreateMutation(integrationId);
  const { mutate: updateMappingMutation } =
    useHubspotMappingUpdateMutation(integrationId);

  useEffect(() => {
    const transformedResources = data?.map(
      (each) =>
        ({
          ...each,
          id: each.resourceMappingConfigId.toString(),
          fieldMappings: each.fieldMappings
            ?.map(
              (fieldMapping) =>
                ({
                  ...fieldMapping,
                  id: fieldMapping?.fieldMappingConfigId?.toString(),
                  hubspotField: {
                    value: fieldMapping?.externalFieldId?.toString(),
                    valueStr: fieldMapping?.externalFieldId?.toString(),
                    label: fieldMapping.externalFieldName,
                  },
                  rocketlaneField: {
                    value: fieldMapping?.nativeFieldId?.toString(),
                    valueStr: fieldMapping?.nativeFieldId?.toString(),
                    label:
                      fieldMapping.nativeFieldLabel ??
                      fieldMapping.nativeFieldName,
                    name: fieldMapping.nativeFieldName,
                  },
                  syncField: fieldMapping?.syncType?.toString(),
                } as HubspotFieldMappingLocalData),
            )
            .sort((a, b) =>
              numberSorter(a?.fieldMappingConfigId, b?.fieldMappingConfigId),
            ),
        } as HubspotResourceMappingLocalData),
    );

    setResources(keyBy(transformedResources, 'id'));
  }, [data]);

  /**
   * Adding Resource
   * @param resourceObject
   */
  const onResourceAdd = (resourceObject: HubspotResourceMapping) => {
    if (resourceObject == null) {
      return false;
    }

    return setResources((savedResources) => ({
      ...savedResources,
      [resourceObject.resourceMappingConfigId]: resourceObject,
    }));
  };

  /**
   * Removing Resource
   * @param resourceId
   */
  const onResourceRemove = (resourceId: number) => {
    if (resourceId == null) {
      return false;
    }

    const removedResource = resources[resourceId];

    setResources((savedResources) =>
      omit(savedResources, [resourceId.toString()]),
    );

    return deleteResourceMutation(resourceId, {
      onSuccess: () => {
        toastService({
          title: successTitle,
          message: resourceDeleteSuccessMessage,
        }).success();
      },
      onError: () => {
        setResources((savedResources) => ({
          ...savedResources,
          [resourceId]: removedResource,
        }));
      },
    });
  };

  /**
   * Adding Field Mapping to resource
   * @param resourceId
   */
  const onMappingAdd = (
    resourceId: number,
    externalField: SingleValue<string>,
  ) => {
    if (resourceId == null) {
      return false;
    }

    let updatedResource = { ...resources[resourceId] };
    const hasFieldMappings = Object.prototype.hasOwnProperty.call(
      updatedResource,
      'fieldMappings',
    );

    if (!hasFieldMappings) {
      updatedResource = {
        ...updatedResource,
        fieldMappings: [],
      };
    }

    updatedResource?.fieldMappings?.push(
      getNewMap(
        integrationId,
        Number(updatedResource.id),
        externalField,
      ) as HubspotFieldMappingLocalData,
    );

    return setResources((_mappings) => ({
      ..._mappings,
      [resourceId]: { ...updatedResource },
    }));
  };

  /**
   * Removing mapping from a resource
   * @param resourceId
   * @param fieldMapping
   */
  const onMappingRemove = async (
    resourceId: number,
    fieldMapping: HubspotFieldMappingLocalData,
  ) => {
    if (resourceId == null || fieldMapping == null) {
      return false;
    }

    const updatedResource = { ...resources[resourceId] };
    const removedMapping = resources[resourceId]?.fieldMappings?.find(
      (fieldMap) => fieldMap.id === fieldMapping.id,
    );

    // remove the mapping in the local data
    await setResources((_mappings) => {
      return {
        ..._mappings,
        [resourceId]: {
          ...updatedResource,
          fieldMappings: updatedResource.fieldMappings.filter(
            (fieldMap) => fieldMap.id !== fieldMapping.id,
          ),
        },
      };
    });

    // if the mapping is already save, make an API call
    if (fieldMapping.fieldMappingConfigId != null) {
      await deteleMappingMutation(
        { resourceId, mappingId: fieldMapping.fieldMappingConfigId },
        {
          onSuccess: () => {
            toastService({
              title: successTitle,
              message: mappingDeleteSuccessMessage,
            }).success();
          },
          onError: () => {
            toastService({
              title: errorTitle,
              message: errorMessage,
            }).error();

            setResources((_resources) => ({
              ..._resources,
              [resourceId]: {
                ...updatedResource,
                fieldMappings: [
                  ...updatedResource.fieldMappings,
                  removedMapping,
                ],
              },
            }));
          },
        },
      );
    }

    return resources;
  };

  /**
   * Changing or updating the fields mapping
   * @param resourceId
   * @param mappingId
   * @param payload
   */
  const onMappingChange = async (
    resourceId: number,
    mappingId: string,
    payload: PartialHubspotFieldMappingLocalData,
  ) => {
    if (resourceId == null || mappingId == null || payload == null) {
      return false;
    }

    const updatedResource = {
      ...resources[resourceId],
    } as HubspotResourceMappingLocalData;

    if (updatedResource != null) {
      const updatedMappingsIndex = (
        updatedResource.fieldMappings ?? []
      ).findIndex((fieldMapping) => fieldMapping.id === mappingId.toString());

      if (
        updatedMappingsIndex >= 0 &&
        updatedResource.fieldMappings[updatedMappingsIndex] != null
      ) {
        updatedResource.fieldMappings[updatedMappingsIndex] =
          payload as HubspotFieldMappingLocalData;

        // update locally
        await setResources((savedMappings) => ({
          ...savedMappings,
          [resourceId]: updatedResource,
        }));

        const serialisedMappingData = serialiseMappingData(
          updatedResource.fieldMappings[updatedMappingsIndex],
        );

        if (serialisedMappingData != null) {
          if (serialisedMappingData?.fieldMappingConfigId) {
            updateMappingMutation(
              {
                resourceId,
                fieldMappingId: serialisedMappingData.fieldMappingConfigId,
                mappingData: serialisedMappingData,
              },
              {
                onError: () => {
                  toastService({
                    title: errorTitle,
                    message: mappingUpdateErrorMessage,
                  }).error();
                },
              },
            );
          } else {
            createMappingMutation(
              {
                resourceId,
                mappingData: serialisedMappingData,
              },
              {
                onError: () => {
                  toastService({
                    title: errorTitle,
                    message: mappingCreateErrorMessage,
                  }).error();
                },
              },
            );
          }
        }
      }
    }

    return resources;
  };

  const resourceTypeOptions = useMemo(
    () =>
      resourceTypeList?.map((salseforceObject) => ({
        label: salseforceObject.name,
        value: salseforceObject.id,
      })),
    [resourceTypeList],
  ) as Option[];

  return {
    resources,
    resourceMappingsStatus,
    resourceTypeOptions,
    resourceTypeStatus,
    onMappingAdd,
    onResourceAdd,
    onResourceRemove,
    onMappingRemove,
    onMappingChange,
  };
};
